import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, Alert } from 'antd';
import useIsNHS from '@hooks/useIsNHS';
import { Form, FormInstance, FormItemProps } from 'ALIASED-antd';
import useUser from '@vl-core/hooks/useUser';
import { useRuntimeConfig } from '@vl-core/useRuntimeConfig';
import { Case } from 'vl-common/src/schemas/shared';
import { NHSNumberFormItem } from './NHSNumberFormItem';

interface ConsentToShareWithGPFormItemsProps {
  FormItemComponent: React.ComponentType<FormItemProps>;
  form: FormInstance;
  summaryLettersFieldName?: string;
  disabled?: boolean;
  nhsNumberFieldName?: string;
  isConsentAllowed?: boolean;
  saveOnChange?: () => void;
  caseDetails?: Case;
}

export const ConsentToShareWithGPFormItems = ({
  FormItemComponent,
  form,
  summaryLettersFieldName = 'gp_summary_consent',
  disabled = false,
  nhsNumberFieldName = 'nhs_number',
  isConsentAllowed,
  saveOnChange = () => {},
  caseDetails
}: ConsentToShareWithGPFormItemsProps) => {
  const { t } = useTranslation();
  const isNHS = useIsNHS();
  const user = useUser();
  const { DOCMAN } = useRuntimeConfig();

  const lettersConsent = Form.useWatch(summaryLettersFieldName, { form, preserve: true });

  useEffect(() => {
    if (caseDetails?.docman_consent_date === null) {
      form.setFieldValue(summaryLettersFieldName, true);
      return;
    }
    form.setFieldValue(summaryLettersFieldName, caseDetails?.docman_consent);
  }, [caseDetails?.docman_consent_date, caseDetails?.docman_consent, form, summaryLettersFieldName]);

  const prevConsentRef = useRef(lettersConsent);
  const [showConsentWarning, setShowConsentWarning] = useState(false);

  useEffect(() => {
    if (!caseDetails?.docman_consent && lettersConsent === true && caseDetails?.docman_consent_date != null) {
      setShowConsentWarning(true);
    }
    prevConsentRef.current = lettersConsent;
  }, [lettersConsent]);

  useEffect(() => {
    const hasLostConsent = prevConsentRef.current && !isConsentAllowed;

    if (!isNHS && hasLostConsent) {
      form.setFieldValue(nhsNumberFieldName, user.nhs_number);
    }

    prevConsentRef.current = isConsentAllowed;
  }, [form, isConsentAllowed, isNHS, nhsNumberFieldName]);

  useEffect(() => {
    if (!user || !saveOnChange) return;
    if (lettersConsent === undefined && user.gp_summary_consent !== undefined) return;

    if (lettersConsent !== user.gp_summary_consent) {
      saveOnChange?.();
    }
  }, [lettersConsent]);

  if (!DOCMAN) return null;

  return (
    <>
      {showConsentWarning && lettersConsent && (
        <Alert
          type="warning"
          showIcon
          message={t('Consent Changed')}
          description={t(
            'All previous relevant documentation for this case will be automatically shared with your NHS GP.'
          )}
          style={{ marginBottom: '1em' }}
        />
      )}

      <div
        style={{
          position: 'relative',
          top: '-0.5em',
          paddingTop: '1.5em',
          fontSize: '0.9rem',
          marginBottom: '0px'
        }}
      >
        {!disabled && (
          <>
            <p>
              {t('You can consent to automatically sharing your clinical letters and documents with your GP via')}{' '}
              <span style={{ fontWeight: 'bold' }}>{t('Docman')}</span>
              {t(', a secure electronic document management system.')}
            </p>
            {lettersConsent && (
              <p>
                {t('We will need your')} <span style={{ fontWeight: 'bold' }}> {t('NHS number')}</span>{' '}
                {t('to do this:')}
              </p>
            )}
          </>
        )}
      </div>

      {lettersConsent && (
        <NHSNumberFormItem
          FormItemComponent={FormItemComponent}
          form={form}
          nhsNumberFieldName="nhs_number"
          membershipFieldName="mem_no"
          patientNHSNumber={caseDetails?.patient_nhs_number || undefined}
        />
      )}

      <FormItemComponent
        required
        layout="vertical"
        style={{ marginTop: '1em' }}
        name={summaryLettersFieldName}
        rules={[
          {
            required: true,
            message: `${t('Please indicate whether you consent to sharing all relevant documentation with your NHS GP')}`
          }
        ]}
      >
        <Radio.Group disabled={disabled}>
          <Radio value={true}>
            {t('I consent to automatically sharing all relevant documentation relating to my case with my NHS GP')}
          </Radio>
          <Radio value={false}>
            {t(
              'I do not consent to automatically sharing all relevant documentation relating to my case with my NHS GP'
            )}
          </Radio>
        </Radio.Group>
      </FormItemComponent>

      {!lettersConsent && (
        <Alert
          type="warning"
          showIcon
          message=""
          description={t(
            'In general, we believe that it is very important that your GP is kept up to date with all of your clinical appointments and the plans and decisions arising from these. However, we understand that sometimes you may not want to share these, and this is entirely your decision'
          )}
          style={{ marginBottom: '1em' }}
        />
      )}
    </>
  );
};
